import type { EditorNodesOptions } from 'platejs'

import type { EditorEntityData } from '../helpers/EditorEntityData'
import { ParagraphElement } from '../plugins/paragraph/nodes/ParagraphElement'
import type { BufferEditor } from './types.plate'
import type { BufferPluginOptions } from '../plugins/types'

type DeserializeOptions = Pick<EditorNodesOptions, 'at' | 'reverse'> & {
  entities?: EditorEntityData
}

// Hydrate inline entities (links, mentions, annotations) from the serialized
// entity data by running each plugin's `textToNodes` callback.
const executeTextToNodesCallbacks = (
  editor: BufferEditor,
  options?: DeserializeOptions,
): void => {
  editor.meta.pluginList.forEach((plugin) => {
    const textToNodes = (plugin.options as BufferPluginOptions | undefined)
      ?.textToNodes
    if (textToNodes) {
      textToNodes(editor, options)
    }
  })
}

/**
 * Deserialize a stored post string into the editor — the inverse of
 * {@link serialize}.
 *
 * `serialize` produces `{ text, data }`; this rebuilds the value from both
 * halves: `text` is split into `ParagraphElement` blocks (one paragraph per
 * line, so triple-click selects a single paragraph — CORE-5280) and `data`
 * hydrates the inline entity nodes (links, mentions, annotations). Loading a
 * post is deserialization, not text input, so we build the value directly
 * rather than feeding the raw string through `editor.insertText`, which never
 * splits on `\n` and collapsed every post into one block.
 *
 * Scope is document content only: history, marks and other session state are
 * not part of the serialized payload and are left untouched (callers manage
 * history via `withoutSavingHistory` / `clearEditorHistory`).
 */
export const deserialize = (
  editor: BufferEditor,
  text = '',
  data?: EditorEntityData,
): void => {
  // Replacing `editor.children` directly is Slate's documented way to load an
  // external value; the cursor collapse + onChange below flush the change so
  // the controlled <Plate> re-renders.
  editor.children = ParagraphElement.paragraphsFromText(text)

  const end = editor.api.end([])
  if (end) editor.tf.select({ anchor: end, focus: end })
  editor.api.onChange()

  executeTextToNodesCallbacks(editor, { entities: data })
}
